import React from 'react';

import Button from 'components/atoms/Button';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { getHtmlText } from 'components/organisms/HtmlSlate';

import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';

import { findInDictionary } from 'helpers/dictionary';
import { priceFormat } from 'helpers/price';

import { IDictionary } from 'models/dictionary';
import {
  ILicencesTemplateResponseContent,
  ILicenceTemplateApplicationProcessDocument,
  ILicenceTemplateApplicationProcessIntelligenceReport,
  ILicenceTemplateApplicationProcessResponse,
  ILicenceTemplateApplicationProcessTask,
  ILicenceTemplatePrintoutSettingsAvailableFields,
  ILicenceTemplateResponse,
} from 'models/licenceConfigurator';
import { ITableColumns, ITableDataSource } from 'models/table';

import { formatDate } from 'utils/date';
import { LETypes } from 'constants/LegalEntityTypes';
import { EPermission } from 'models/permissions';

export const licenceDataFields = [
  'licenceName',
  'licenceNumber',
  'validityPeriod',
  'licenceCategory',
  'carryConditions',
  'region',
  'goodReason',
  'competency',
  'weaponNumberLimit',
  'licencingAuthority',
  'licencingAuthoritySignature',
  'additionalConditions',
];

export const legalEntityDataFields = [
  'legalEntityIndividualId',
  'legalEntityOrganisationId',
  'organisationName',
  'dateOfBirth',
  'placeOfBirth',
  'nationality',
  'address',
  'height',
  'occupation',
];

export const weaponDataFields: string[] = [
  'urn',
  'type',
  'make',
  'model',
  'calibre',
  'serialNumber',
  'weaponCategory',
  'action',
];

export const organisationRequired = ['organisationName', 'legalEntityOrganisationId'];

export const individualRequired = [
  'legalEntityIndividualId',
  'dateOfBirth',
  'placeOfBirth',
  'nationality',
  'height',
  'occupation',
];

export const ownerLegalEntityOrganisationDataFields = [
  'legalEntityId',
  'organisationName',
  'primaryAddress',
  'organisationType',
  'departmentName',
];

export const ownerLegalEntityOrganisationDisable = [
  'legalEntityId',
  'organisationName',
  'primaryAddress',
];

export const ownerLegalEntityIndividualDataFields = [
  'legalEntityId',
  'fullName',
  'secondSurname',
  'primaryAddress',
  'gender',
  'documentTypeAndNumber',
  'dateOfBirth',
  'placeOfBirth',
  'nationality',
  'height',
  'occupation',
];

export const documentLegalEntityIndividualDataFields = [
  'legalEntityIndividualId',
  'fullName',
  'individualPrimaryAddress',
  'gender',
  'photo',
  'documentTypeAndNumber',
  'dateOfBirth',
  'placeOfBirth',
  'nationality',
  'height',
  'occupation',
  'secondSurname',
];

export const documentLegalEntityOrganisationDataFields = [
  'legalEntityOrganisationId',
  'organisationName',
  'organisationPrimaryAddress',
  'organisationType',
  'departmentName',
];

export const ownerLegalEntityIndividualDisable = [
  'legalEntityId',
  'fullName',
  'primaryAddress',
  'gender',
  'photo',
];

export const printoutSettingsGeneralFields = [
  'licenceName',
  'licenceCode',
  'licenceNumber',
  'licencePeriod',
  'issueDate',
  'issuedLicencingAuthorityName',
  'issuedLicencingAuthorityPerson',
  'issuedLicencingAuthoritySignature',
];

export const printoutSettingsConditionsFields = [
  'areaRestriction',
  'competency',
  'safeStorage',
  'weaponCategorisation',
  'weaponNumberLimit',
  'carryConditions',
  'statutoryAndAdditionalConditions',
  'goodReasons',
];

export const printoutSettingsOwnerFields = [
  'legalType',
  'fullName',
  'primaryAddress',
  'gender',
  'documentTypeAndName',
  'dateOfBirth',
  'placeOfBirth',
  'nationality',
  'height',
  'occupation',
  'photo',
  'secondSurname',
  'organisationType',
  'organisationName',
  'departmentName',
];
export const printoutSettingsWeaponDataFields = [
  'type',
  'make',
  'model',
  'calibre',
  'action',
  'manufacturer',
  'serialNumber',
];

export enum ELicenceFormLabels {
  name = 'licence.name.label',
  code = 'licence.code.label',
  comment = 'licence.comment.label',
  codeAndName = 'licence.codeAndName.label',
  definitionStatus = 'licence.definitionStatus.label',
  codeOrName = 'licenceConfigurator.search.placeholder.text',
  description = 'licence.description.label',
  processTypes = 'licence.processTypes.label',
  priceName = 'licence.priceName.label',
  priceDescription = 'licence.priceDescription.label',
  processTypesApplication = 'licence.processTypes.applyfor.label',
  processTypesRenewal = 'licence.processTypes.renewal.label',
  processTypesVariation = 'licence.processTypes.variations.label',
  relatedLegalEntityType = 'licence.relatedLegalEntityType.label',
  legalEntityType = 'licence.legalEntityType.label',
  licenceFee = 'licence.licenceFee.label',
  grantFee = 'licence.grantFee.label',
  renewalFee = 'licence.renewalFee.label',
  variationFee = 'licence.variationFee.label',
  licenceDurationMonths = 'licence.licenceDurationMonths.label',
  licenceDurationYears = 'licence.licenceDurationYears.label',
  licenceDurationDays = 'licence.licenceDurationDays.label',
  licenceDuration = 'licence.licenceDuration.label',
  licenceDurationUnlimited = 'licence.licenceDurationUnlimited.label',
  licenceDurationLimited = 'licence.licenceDurationLimited.label',
  ageLimit = 'licence.individualAgeLimit.label',
  goodReason = 'licence.goodReason.label',
  goodReasonDictionaryIds = 'licence.goodReasonDictionaryIds.label',
  goodReasonDictionary = 'licence.goodReason.dictionary.label',
  goodReasonText = 'licence.goodReason.string.label',
  goodReasonList = 'licence.goodReason.list.label',
  goodReasonObject = 'licence.goodReason.object.label',
  specificCountryArea = 'licence.specificCountryArea.label',
  competency = 'licence.competency.label',
  safeStorage = 'licence.safeStorage.label',
  safeStorageText = 'licence.safeStorage.string.label',
  safeStorageObject = 'licence.safeStorage.object.label',
  weaponCategory = 'licence.weaponCategory.label',
  weaponNumberLimit = 'licence.weaponNumberLimit.label',
  carryConditions = 'licence.carryConditions.label',
  additionalConditions = 'licence.additionalConditions.label',
  licenceData = 'licence.licenceData.label',
  legalEntityData = 'licence.legalEntityData.label',
  weaponData = 'licence.weaponData.label',
  statusUniqueId = 'licence.status.label',
  legalTypes = 'licence.legalTypes.label',
  createDateFrom = 'licence.createDateFrom.label',
  createDateTo = 'licence.createDateTo.label',
}

export enum ELicenceCardInformationFormLabels {
  licenceName = 'licence.licenceName.label',
  licenceNumber = 'licence.licenceNumber.label',
  validityPeriod = 'licence.validityPeriod.label',
  licenceCategory = 'licence.licenceCategory.label',
  carryConditions = 'licence.carryConditions.label',
  region = 'licence.region.label',
  goodReason = 'licence.goodReason.label',
  competency = 'licence.competency.label',
  weaponNumberLimit = 'licence.weaponNumberLimit.label',
  licencingAuthority = 'licence.licencingAuthority.label',
  licencingAuthoritySignature = 'licence.licencingAuthoritySignature.label',
  additionalConditions = 'licence.additionalConditions.label',
  legalEntityIndividualId = 'licence.legalEntityIndividualId.label',
  legalEntityOrganisationId = 'licence.legalEntityOrganisationId.label',
  organisationName = 'licence.organisationName.label',
  dateOfBirth = 'licence.dateOfBirth.label',
  placeOfBirth = 'licence.placeOfBirth.label',
  nationality = 'licence.nationality.label',
  address = 'licence.address.label',
  photo = 'licence.photo.label',
  height = 'licence.height.label',
  occupation = 'licence.occupation.label',
  urn = 'licence.urn.label',
  manufacturer = 'licence.manufacturer.label',
  make = 'licence.make.label',
  model = 'licence.model.label',
  calibre = 'licence.calibre.label',
  serialNumber = 'licence.serialNumber.label',
  type = 'licence.type.label',
  weaponCategory = 'licence.weaponCategory.label',
  action = 'licence.action.label',
  legalEntityId = 'licence.legalEntityId.label',
  primaryAddress = 'licence.primaryAddress.label',
  organisationPrimaryAddress = 'licence.primaryAddress.label',
  individualPrimaryAddress = 'licence.primaryAddress.label',
  organisationType = 'licence.organisationType.label',
  fullName = 'licence.fullName.label',
  gender = 'licence.gender.label',
  documentTypeAndNumber = 'licence.documentTypeAndNumber.label',
  licenceCode = 'licence.licenceCode.label',
  licencePeriod = 'licence.licencePeriod.label',
  issueDate = 'licence.issueDate.label',
  issuedLicencingAuthorityName = 'licence.issuedLicencingAuthorityName.label',
  issuedLicencingAuthorityPerson = 'licence.issuedLicencingAuthorityPerson.label',
  issuedLicencingAuthoritySignature = 'licence.issuedLicencingAuthoritySignature.label',
  areaRestriction = 'licence.areaRestriction.label',
  weaponCategorisation = 'licence.weaponCategorisation.label',
  statutoryAndAdditionalConditions = 'licence.statutoryAndAdditionalConditions.label',
  goodReasons = 'licence.goodReasons.label',
  legalType = 'licence.legalType.label',
  documentTypeAndName = 'licence.documentTypeAndName.label',
  departmentName = 'licence.departmentName.label',
  safeStorage = 'licence.safeStorage.label',
  secondSurname = 'licence.secondSurname.label',
}

export const initialFormValues = {
  general: {
    name: '',
    description: '',
    code: '',
    licenceDurationYears: 0,
    licenceDurationMonths: 0,
    licenceDurationDays: 0,
    licenceDurationUnlimited: false,
  },
  owner: {
    legalTypes: {
      INDIVIDUAL: false,
      ORGANIZATION: false,
    },
    individual: {
      legalEntityId: false,
      fullName: false,
      primaryAddress: false,
      dateOfBirth: false,
      placeOfBirth: false,
      nationality: false,
      height: false,
      occupation: false,
      secondSurname: false,
      documentTypeAndNumber: false,
      gender: false,
      ageLimit: 0,
    },
    organisation: {
      legalEntityId: false,
      organisationName: false,
      organisationType: false,
      departmentName: false,
      primaryAddress: false,
    },
  },
  conditions: {
    goodReason: 'false',
    goodReasonDictionaryIds: [],
    specificCountryArea: false,
    competency: false,
    safeStorage: 'false',
    weaponCategory: false,
    weaponNumberLimit: false,
    carryConditions: false,
    additionalConditions: false,
  },
  documentInformation: {
    licenceName: false,
    licenceNumber: false,
    validityPeriod: false,
    licenceCategory: false,
    carryConditions: false,
    region: false,
    goodReason: false,
    competency: false,
    weaponNumberLimit: false,
    licencingAuthority: false,
    licencingAuthoritySignature: false,
    additionalConditions: false,
    legalEntityIndividualId: false,
    legalEntityOrganisationId: false,
    organisationPrimaryAddress: false,
    individualPrimaryAddress: false,
    organisationName: false,
    organisationType: false,
    departmentName: false,
    fullName: false,
    dateOfBirth: false,
    placeOfBirth: false,
    nationality: false,
    gender: false,
    photo: false,
    height: false,
    occupation: false,
    urn: false,
    type: false,
    make: false,
    model: false,
    calibre: false,
    serialNumber: false,
    weaponCategory: false,
    action: false,
    documentTypeAndNumber: false,
  },
};

export const initialFormValuesApplicationProcess = {
  licenceTemplateUniqueId: '',
  priceName: '',
  priceFee: '',
  processTypeUniqueId: '',
  priceDescription: '',
  legalEntityType: null,
  comment: '',
};
export const initialFormValuesApplicationProcessPrice = {
  licenceTemplateUniqueId: '',
  priceName: '',
  priceFee: '',
  priceDescription: '',
};
export const initialFormValuesApplicationProcessSettings = {
  licenceTemplateUniqueId: '',
  legalEntityType: null,
  comment: '',
};

export interface ILicenceConfigurationSearchFormValues {
  preset: string;
  name: string;
  code: string;
  statusUniqueId: string;
  leType: string;
  createDateFrom: string;
  createDateTo: string;
}

export const initialSearchFormValues = {
  preset: '',
  name: '',
  code: '',
  statusUniqueId: '',
  leType: '',
  createDateFrom: '',
  createDateTo: '',
};

export const mapLicenceApplicationProcessToDataSource = (
  items: ILicenceTemplateApplicationProcessResponse[],
  applicationProcessTypes: IDictionary[] | null,
  applicationProcessStatus: IDictionary[] | null,
  currency: string,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    type: findInDictionary(item.processTypeUniqueId, applicationProcessTypes),
    typeId: item.processTypeUniqueId,
    legalEntityType: item.legalEntityType,
    price: `${priceFormat(item.priceFee)} ${currency}`,
    name: item.priceName,
    status: findInDictionary(item.statusUniqueId, applicationProcessStatus),
    statusUniqueId: item.statusUniqueId,
    description: getHtmlText(item.priceDescription, 80),
  }));

export enum EApplicationProcessStatus {
  active = 'ebf680d5-18ae-48df-a631-c74e6624765e',
  inactive = '61e60fae-cf6e-4505-91b1-17d8914170c0',
  draft = '5c160e87-1057-40de-b980-90134005ba76',
}

export const statusApplicationProcessChipVariant = (
  statusUniqueId: string,
): EChipsVariants | undefined => {
  switch (statusUniqueId) {
    case EApplicationProcessStatus.active:
      return EChipsVariants.success;
    case EApplicationProcessStatus.inactive:
      return EChipsVariants.error;
    case EApplicationProcessStatus.draft:
      return EChipsVariants.inactive;
    default:
      return EChipsVariants.primary;
  }
};

interface ILicenceApplicationProcessTableColumns {
  handleView: (arg: string) => void;
  handleDelete: (arg: string) => void;
  t: any;
}

export const LicenceApplicationProcessTableColumns = ({
  handleView,
  handleDelete,
  t,
}: ILicenceApplicationProcessTableColumns): ITableColumns[] => [
  {
    id: '36fa4777-5f90-434a-adf1-2b3aea11d619',
    key: 'type',
    title: 'licence.processType.type.column',
    width: '120px',
    isSortable: false,
  },
  {
    id: '36fa4777-5f90-434a-adf1-2b3aea11d621',
    key: 'legalEntityType',
    title: 'licence.processType.legalEntityType.column',
    width: '140px',
    isSortable: false,
    render: (_data, props) =>
      props.legalEntityType === LETypes.individual
        ? t('legalEntity.individual.label')
        : t('legalEntity.organisation.label'),
  },
  {
    id: '36fa4777-5f90-434a-adf1-2b3aea11d622',
    key: 'status',
    title: 'licence.processType.status.column',
    width: '100px',
    isSortable: false,
    justifyContentHead: 'center',
    render: (_data, props) => (
      <Chips
        text={props.status}
        variant={statusApplicationProcessChipVariant(props.statusUniqueId)}
        fullWidth
      />
    ),
  },
  {
    id: 'c3c4e92a-8276-4695-9b6d-9c3566e95abc',
    key: 'price',
    title: 'licence.licencePricesTable.price.column',
    width: '120px',
    isSortable: false,
  },
  {
    id: '999918e4-7492-40b2-9445-4d8d43e24f07',
    key: 'name',
    width: '160px',
    title: 'licence.licencePricesTable.priceName.column',
    isSortable: false,
  },
  {
    id: 'a463ea1c-4c9f-4c64-aac4-01fb85c2bb66',
    key: 'description',
    title: 'licence.licencePricesTable.priceDescription.column',
    isSortable: false,
  },
  {
    id: '9c4ca471-15cd-4e22-90ef-7b57b7622955',
    key: '',
    title: 'licence.licencePricesTable.action.column',
    isSortable: false,
    width: '110px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <>
        <Button
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.view}
          onClick={() => handleView(props.id)}
        />
        {props.statusUniqueId === EApplicationProcessStatus.draft && (
          <Button
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.delete}
            onClick={() => handleDelete(props.id)}
            permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
          />
        )}
      </>
    ),
  },
];

export enum EWeaponLicenceTemplateStatusId {
  active = '8b4f9e3c-6d5a-4f01-9b2e-1a2b3c4d5e61',
  draft = '8b4f9e3c-6d5a-4f01-9b2e-1a2b3c4d5e60',
  inactive = '8b4f9e3c-6d5a-4f01-9b2e-1a2b3c4d5e62',
}

export const getStatusVariant = (statusId: string) => {
  switch (statusId) {
    case EWeaponLicenceTemplateStatusId.active:
      return EChipsVariants.success;
    case EWeaponLicenceTemplateStatusId.inactive:
      return EChipsVariants.error;
    case EWeaponLicenceTemplateStatusId.draft:
      return EChipsVariants.inactive;
    default:
      break;
  }
};

export const mapLicenceConfiguratorDataSource = (
  items: ILicencesTemplateResponseContent[],
  weaponLicenceTemplateStatus: IDictionary[] | null,
) =>
  items.map((item) => ({
    id: item.uniqueId,
    code: item.code,
    name: item.name,
    status: findInDictionary(item.statusUniqueId, weaponLicenceTemplateStatus),
    statusId: item.statusUniqueId,
    licenceDurationYears: item.licenceDurationYears,
    licenceDurationMonths: item.licenceDurationMonths,
    licenceDurationDays: item.licenceDurationDays,
    licenceDurationUnlimited: item.licenceDurationUnlimited,
    legalTypes: item.legalTypes,
    createdDate: item.createdDate,
  }));

interface ILicenceConfiguratorTableColumns {
  handleView: (arg: string) => void;
  dateFormat: string;
  t: any;
}

export const licenceConfiguratorTableColumns = ({
  handleView,
  dateFormat,
  t,
}: ILicenceConfiguratorTableColumns): ITableColumns[] => [
  {
    id: '36fa4777-5f90-434a-adf1-2b3aea11d685',
    key: 'code',
    title: 'licence.code.label',
  },
  {
    id: 'c3c4e92a-8276-4695-9b6d-9c3566e9501a',
    key: 'name',
    title: 'licenceConfiguration.licenceName.label',
  },
  {
    id: '144918e4-7492-40b2-9445-4d8d43e24f07',
    key: 'statusUniqueId',
    title: 'licenceConfigurator.status.column',
    render: (_data, props) => (
      <Chips variant={getStatusVariant(props.statusId)} text={props.status} fullWidth />
    ),
  },
  {
    id: '063918e4-7492-40b2-9445-4d8d43e24194',
    key: 'duration',
    title: 'licenceConfigurator.duration.column',
    render: (_data, props) => {
      if (props.licenceDurationUnlimited) {
        return (
          <Chips
            variant={EChipsVariants.success}
            text={t('licenceConfiguration.duration.unlimited')}
            fullWidth
          />
        );
      }

      const duration: string[] = [];

      if (props.licenceDurationYears) {
        duration.push(
          `${props.licenceDurationYears} ${
            props.licenceDurationYears > 1
              ? t('licenceConfiguration.duration.years')
              : t('licenceConfiguration.duration.year')
          }`,
        );
      }

      if (props.licenceDurationMonths) {
        duration.push(
          `${props.licenceDurationMonths} ${
            props.licenceDurationMonths > 1
              ? t('licenceConfiguration.duration.months')
              : t('licenceConfiguration.duration.month')
          }`,
        );
      }

      if (props.licenceDurationDays) {
        duration.push(
          `${props.licenceDurationDays} ${
            props.licenceDurationDays > 1
              ? t('licenceConfiguration.duration.days')
              : t('licenceConfiguration.duration.day')
          }`,
        );
      }

      return duration.join(', ');
    },
  },
  {
    id: 'a463ea1c-4c9f-4c64-aac4-01fb85c2bb66',
    key: 'legalTypes',
    title: 'licenceConfiguration.leType.column',
    isSortable: false,
    render: (_data, props) =>
      props?.legalTypes.map(
        (type: string, key: number) =>
          `${t(`licence.userType.${type.toLowerCase()}.label`)}${
            props?.legalTypes.length > key + 1 ? ', ' : ''
          } `,
      ),
  },
  {
    id: '063918e4-7492-40b2-9445-4d8d43e24f07',
    key: 'createdDate',
    width: '130px',
    title: 'licenceConfigurator.createDate.column',
    render: (_data, props) => formatDate(props.createdDate, dateFormat),
  },
  {
    id: '9c4ca471-15cd-4e22-90ef-7b57b7622955',
    key: '',
    title: 'licence.licenceTable.action.column',
    isSortable: false,
    width: '80px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Button
        id={`licence_configuration-view-${props.id}`}
        variant={EButtonVariants.outlined}
        tableButton
        icon={EIconTypes.view}
        onClick={() => handleView(props.id)}
      />
    ),
  },
];

interface IApplicationProcessTaskTableColumns {
  handleDelete?: (arg: string) => void;
}

export const ApplicationProcessTaskTableColumns = ({
  handleDelete,
}: IApplicationProcessTaskTableColumns): ITableColumns[] => [
  {
    id: '999918e4-7492-40b2-9445-4d8d43e24f07',
    key: 'name',
    width: '160px',
    title: 'licenceConfigurator.task.name.column',
    isSortable: false,
  },
  {
    id: 'a463ea1c-4c9f-4c64-aac4-01fb85c2bb66',
    key: 'description',
    title: 'licenceConfigurator.task.description.column',
    isSortable: false,
  },
  ...(!!handleDelete
    ? [
        {
          id: '9c4ca471-15cd-4e22-90ef-7b57b7622955',
          key: '',
          title: 'licenceConfigurator.task.action.column',
          isSortable: false,
          width: '80px',
          isCenterTableHead: true,
          render: (_data: any, props: { id: string }) => (
            <Button
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              onClick={() => handleDelete(props.id)}
            />
          ),
        },
      ]
    : []),
];

export const mapApplicationProcessTask = (
  items: ILicenceTemplateApplicationProcessTask[],
  task: IDictionary[] | null,
) =>
  items.map((item) => ({
    id: item.uniqueId,
    name: findInDictionary(item.nameUniqueId, task),
    description: item.description,
  }));
interface IApplicationProcessDocumentTableColumns {
  handleDelete?: (arg: string) => void;
}

export const ApplicationProcessDocumentTableColumns = ({
  handleDelete,
}: IApplicationProcessDocumentTableColumns): ITableColumns[] => [
  {
    id: '999918e4-7492-40b2-9445-4d8d43e24f07',
    key: 'type',
    width: '160px',
    title: 'licenceConfigurator.document.type.column',
    isSortable: false,
  },
  {
    id: 'a463ea1c-4c9f-4c64-aac4-01fb85c2bb66',
    key: 'importantNotice',
    title: 'licenceConfigurator.document.description.column',
    isSortable: false,
  },
  ...(!!handleDelete
    ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f549',
          key: '',
          title: 'licenceConfigurator.task.action.column',
          isSortable: false,
          width: '80px',
          isCenterTableHead: true,
          render: (_data: any, props: { id: string }) => (
            <Button
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              onClick={() => handleDelete(props.id)}
            />
          ),
        },
      ]
    : []),
];

export const mapApplicationProcessDocument = (
  items: ILicenceTemplateApplicationProcessDocument[],
  attachmentDocumentTypeData: IDictionary[] | null,
) =>
  items.map((item) => ({
    id: item.uniqueId,
    type: findInDictionary(item.typeUniqueId, attachmentDocumentTypeData),
    importantNotice: item.importantNotice,
  }));

interface IApplicationProcessIntelligenceReportTableColumns {
  handleDelete: (arg: string) => void;
  handleEdit: (arg: string) => void;
}

export const ApplicationProcessIntelligenceReportTableColumns = ({
  handleDelete,
  handleEdit,
}: IApplicationProcessIntelligenceReportTableColumns): ITableColumns[] => [
  {
    id: '999918e4-7492-40b2-9445-4d8d43e24f07',
    key: 'sourceSystem',
    width: '160px',
    title: 'licenceConfigurator.intelligenceReport.sourceSystem.column',
    isSortable: false,
  },
  {
    id: 'a463ea1c-4c9f-4c64-aac4-01fb85c2bb66',
    key: 'instructions',
    title: 'licenceConfigurator.intelligenceReport.instructions.column',
    isSortable: false,
  },
  {
    id: '9c4ca471-15cd-4e22-90ef-7b57b7622955',
    key: '',
    title: 'general.actions.text',
    isSortable: false,
    width: '80px',
    isCenterTableHead: true,
    render: (_data: any, props: { id: string }) => (
      <>
        <Button
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.delete}
          onClick={() => handleDelete(props.id)}
          id={`licence_configuration-intelligence_report-delete-${props.id}`}
        />
        <Button
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.edit}
          onClick={() => handleEdit(props.id)}
          id={`licence_configuration-intelligence_report-edit-${props.id}`}
        />
      </>
    ),
  },
];

export const mapApplicationProcessIntelligenceReport = (
  items: ILicenceTemplateApplicationProcessIntelligenceReport[],
  sourceSystem: IDictionary[] | null,
) =>
  items.map((item) => ({
    id: item.uniqueId,
    sourceSystem: findInDictionary(item.sourceSystemUniqueId, sourceSystem),
    instructions: item.instructions,
  }));

export const printoutSettingsFieldFilter = (
  licence: ILicenceTemplatePrintoutSettingsAvailableFields,
  fields: string[],
  name: string,
) => {
  let values = licence?.[
    name as keyof ILicenceTemplatePrintoutSettingsAvailableFields
  ] as Record<string, any>;

  return fields?.filter((field) => field && values?.[field]);
};
